import { useRef } from 'react'
import type { Asset, Composition } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { getSegmentTimeline, pxToSec } from '../../../lib/timelineMath'
import { readSceneDragPayload } from '../dragTypes'
import { SegmentBlock } from './SegmentBlock'
import { SEGMENT_REORDER_MIME, VIDEO_LANE_H } from './constants'

export function VideoLane({
  composition,
  assets,
  pxPerSec,
}: {
  composition: Composition
  assets: Record<string, Asset>
  pxPerSec: number
}) {
  const addSegmentFromScene = useEditorStore((s) => s.addSegmentFromScene)
  const reorderSegments = useEditorStore((s) => s.reorderSegments)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const select = useEditorStore((s) => s.select)
  const laneRef = useRef<HTMLDivElement>(null)

  const timeline = getSegmentTimeline(composition.tracks.video)

  function indexForDropX(clientX: number): number {
    const rect = laneRef.current?.getBoundingClientRect()
    const x = rect ? clientX - rect.left : 0
    const dropSec = pxToSec(x, pxPerSec)
    const idx = timeline.findIndex((t) => dropSec < t.startSec + (t.endSec - t.startSec) / 2)
    return idx === -1 ? timeline.length : idx
  }

  return (
    <div
      ref={laneRef}
      className="relative border-b border-ink-border/40"
      style={{ height: VIDEO_LANE_H, width: '100%' }}
      data-testid="video-lane"
      onClick={(e) => {
        // Segment 自身の onClick は stopPropagation するので、ここまで来るのは
        // 空白（レーンの背景）クリックのみ — task spec §2-3「空白クリックで解除」。
        if (e.target === e.currentTarget) select(null)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        const scenePayload = readSceneDragPayload(e.dataTransfer)
        if (scenePayload) {
          const asset = assets[scenePayload.assetId]
          const scene = asset?.scenes[scenePayload.sceneIndex]
          if (asset && scene) addSegmentFromScene(asset.id, scene, indexForDropX(e.clientX))
          return
        }
        const reorderRaw = e.dataTransfer.getData(SEGMENT_REORDER_MIME)
        if (reorderRaw) {
          const from = Number(reorderRaw)
          let to = indexForDropX(e.clientX)
          if (to > from) to -= 1
          if (!Number.isNaN(from) && from !== to) reorderSegments(from, to)
        }
      }}
    >
      {timeline.length === 0 && (
        <div className="absolute inset-0 flex items-center px-3 text-[11px] text-text-faint">
          ここにシーンをドラッグして追加
        </div>
      )}
      {timeline.map((t) => (
        <SegmentBlock
          key={t.segment.id}
          segment={t.segment}
          index={t.index}
          startSec={t.startSec}
          pxPerSec={pxPerSec}
          asset={assets[t.segment.assetId]}
          section={composition.sections.find((s) => s.id === t.segment.sectionId)}
          selected={selectedIds.some((sel) => sel.kind === 'segment' && sel.id === t.segment.id)}
        />
      ))}
    </div>
  )
}
